import { ApuTiming, Cartridge } from '@/Emulator';
import { PulseChannel }         from '@/Emulator/Hardware/APU/PulseChannel';
import { TriangleChannel }      from '@/Emulator/Hardware/APU/TriangleChannel';

/**
 * Audio Processing Unit
 */
export class APU implements ISerializable
{
    static IsAddressing = {
        Pulse1:       (addr: number): boolean => addr >= 0x4000 && addr <= 0x4003,
        Pulse2:       (addr: number): boolean => addr >= 0x4004 && addr <= 0x4007,
        Triangle:     (addr: number): boolean => addr >= 0x4008 && addr <= 0x400B,
        Noise:        (addr: number): boolean => addr >= 0x400C && addr <= 0x400F,
        DMC:          (addr: number): boolean => addr >= 0x4010 && addr <= 0x4013,
        ApuStatus:    (addr: number): boolean => addr === 0x4015,
        FrameCounter: (addr: number): boolean => addr === 0x4017,
    } as const;

    private readonly pulse1: PulseChannel      = new PulseChannel(1);
    private readonly pulse2: PulseChannel      = new PulseChannel(2);
    private readonly triangle: TriangleChannel = new TriangleChannel();
    // private readonly noise    = new NoiseChannel();
    // private readonly dmc      = new DmcChannel();

    private readonly timing: ApuTiming;

    private frameCounterMode: number = 0;
    private irqInhibit: boolean      = false;
    private frameInterrupt: boolean  = false;
    private cycle: number            = 0;

    constructor(private readonly cartridge: Cartridge)
    {
        this.timing = cartridge.apuTiming;
    }

    public peekRegister(address: number): number
    {
        if (address === 0x4015) {
            let status: number = 0;

            status |= (this.pulse1.lengthCounter > 0 ? 1 : 0) << 0;
            status |= (this.pulse2.lengthCounter > 0 ? 1 : 0) << 1;
            status |= (this.triangle.lengthCounter > 0 ? 1 : 0) << 2;
            // status |= (this.noise.lengthCounter > 0 ? 1 : 0) << 3;
            // status |= (this.dmc.bytesRemaining > 0 ? 1 : 0) << 4;
            status |= (this.frameInterrupt ? 1 : 0) << 6;
            // status |= (this.dmc.interrupt ? 1 : 0) << 7;

            return status;
        }

        return 0;
    }

    public readRegister(address: number): number
    {
        if (address === 0x4015) {
            const status: number = this.peekRegister(address);

            this.frameInterrupt = false;

            return status;
        }

        return 0;
    }

    public writeRegister(address: number, value: number): void
    {
        if (APU.IsAddressing.Pulse1(address)) {
            return this.pulse1.writeRegister(address, value);
        }
        if (APU.IsAddressing.Pulse2(address)) {
            return this.pulse2.writeRegister(address, value);
        }
        if (APU.IsAddressing.Triangle(address)) {
            return this.triangle.writeRegister(address, value);
        }
        if (APU.IsAddressing.Noise(address)) {
            return; // this.noise.writeRegister(address, value);
        }
        if (APU.IsAddressing.DMC(address)) {
            return; // this.dmc.writeRegister(address, value);
        }
        if (APU.IsAddressing.ApuStatus(address)) {
            this.pulse1.setEnabled((value & 0x01) !== 0);
            this.pulse2.setEnabled((value & 0x02) !== 0);
            this.triangle.setEnabled((value & 0x04) !== 0);
            // this.noise.setEnabled((value & 0x08) !== 0);
            // this.dmc.setEnabled((value & 0x10) !== 0);
            return;
        }
        if (APU.IsAddressing.FrameCounter(address)) {
            this.frameCounterMode = (value >> 7) & 1;
            this.irqInhibit       = ((value >> 6) & 1) === 1;

            if (this.irqInhibit) {
                this.frameInterrupt = false;
            }

            this.cycle = 0;

            // Hardware quirk: If Mode 1 is selected, the APU immediately clocks
            //                 the half-frame and quarter-frame signals.
            if (this.frameCounterMode === 1) {
                this.clockQuarterFrame();
                this.clockHalfFrame();
            }
        }
    }

    public tick(): void
    {
        const isEvenCycle: boolean = (this.cycle % 2) === 0;

        if (isEvenCycle) {
            this.pulse1.tickTimer();
            this.pulse2.tickTimer();
            // this.noise.tickTimer();
            // this.dmc.tickTimer();
        }

        this.triangle.tickTimer();

        this.tickFrameCounter();
        this.cycle++;
    }

    public get hasPendingInterruptRequest(): boolean
    {
        return this.frameInterrupt; // || this.dmc.interrupt;
    }

    public get output(): number
    {
        const p1 = this.pulse1.output;
        const p2 = this.pulse2.output;
        const t  = this.triangle.output;
        // const n = this.noise.output;
        // const d = this.dmc.output;

        // --- 1. Pulse Group ---
        let pulseOut = 0;
        if (p1 !== 0 || p2 !== 0) {
            pulseOut = 95.88 / ((8128.0 / (p1 + p2)) + 100.0);
        }

        // --- 2. Triangle, Noise, DMC Group (TND) ---
        let tndOut = 0;
        if (t !== 0 /* || n !== 0 || d !== 0 */) {
            // Note: Once you add Noise and DMC, uncomment their parts of this divisor!
            tndOut = 159.79 / (1.0 / ((t / 8227.0) /* + (n / 12241.0) + (d / 22638.0) */) + 100.0);
        }

        // The result is a clean floating-point number between 0.0 and 1.0
        return pulseOut + tndOut;
    }

    private tickFrameCounter(): void
    {
        if (this.frameCounterMode === 0) {
            // --- Mode 0: 4-Step Sequence ---
            if (this.cycle === this.timing.mode0Steps[0]) {
                this.clockQuarterFrame();
            } else if (this.cycle === this.timing.mode0Steps[1]) {
                this.clockQuarterFrame();
                this.clockHalfFrame();
            } else if (this.cycle === this.timing.mode0Steps[2]) {
                this.clockQuarterFrame();
            } else if (this.cycle === this.timing.mode0Steps[3] - 1 && !this.irqInhibit) {
                this.frameInterrupt = true;
            } else if (this.cycle === this.timing.mode0Steps[3]) {
                this.clockQuarterFrame();
                this.clockHalfFrame();
                if (!this.irqInhibit) {
                    this.frameInterrupt = true;
                }
            } else if (this.cycle === this.timing.mode0Steps[3] + 1) {
                if (!this.irqInhibit) {
                    this.frameInterrupt = true;
                }
                this.cycle = 0; // Reset
            }
        } else {
            // --- Mode 1: 5-Step Sequence ---
            if (this.cycle === this.timing.mode1Steps[0]) {
                this.clockQuarterFrame();
            } else if (this.cycle === this.timing.mode1Steps[1]) {
                this.clockQuarterFrame();
                this.clockHalfFrame();
            } else if (this.cycle === this.timing.mode1Steps[2]) {
                this.clockQuarterFrame();
            } else if (this.cycle === this.timing.mode1Steps[3]) {
                // Step 4 is a dummy step in Mode 1
            } else if (this.cycle === this.timing.mode1Steps[4]) {
                this.clockQuarterFrame();
                this.clockHalfFrame();
            } else if (this.cycle === this.timing.mode1Steps[4] + 1) {
                this.cycle = 0; // Reset
            }
        }
    }

    private clockHalfFrame(): void
    {
        this.pulse1.clockLength();
        this.pulse1.clockSweep();
        this.pulse2.clockLength();
        this.pulse2.clockSweep();
        this.triangle.clockLength();
        // this.noise.clockLength();
        // this.dmc.clockLength();
    }

    private clockQuarterFrame(): void
    {
        this.pulse1.clockEnvelope();
        this.pulse2.clockEnvelope();
        this.triangle.clockLinearCounter();
        // this.noise.clockLinearCounter();
        // this.dmc.clockLinearCounter();
    }

    /**
     * @inheritDoc
     */
    public serialize(): Record<string, any>
    {
        return {
            frameCounterMode: this.frameCounterMode,
            irqInhibit:       this.irqInhibit,
            frameInterrupt:   this.frameInterrupt,
            cycle:            this.cycle,
            pulse1:           this.pulse1.serialize(),
            pulse2:           this.pulse2.serialize(),
            triangle:         this.triangle.serialize(),
            // noise: this.noise.serialize(),
            // dmc: this.dmc.serialize(),
        };
    }

    /**
     * @inheritDoc
     */
    public deserialize(data: Record<string, any>): void
    {
        this.frameCounterMode = data.frameCounterMode;
        this.irqInhibit       = data.irqInhibit;
        this.frameInterrupt   = data.frameInterrupt;
        this.cycle            = data.cycle;

        this.pulse1.deserialize(data.pulse1);
        this.pulse2.deserialize(data.pulse2);
        this.triangle.deserialize(data.triangle);
        // this.noise.deserialize(data.noise);
        // this.dmc.deserialize(data.dmc);
    }
}
